import { JSX, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export enum Environment {
  Development = "development",
  Production = "production",
  Test = "test",
}

const ProtectedRoute = ({ children }: { children: JSX.Element }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Extract token from URL if present.
    const params = new URLSearchParams(location.search);
    const tokenFromUrl = params.get("token");
    
    if (tokenFromUrl) {
      localStorage.setItem("token", tokenFromUrl);
      // Remove token from URL for security
      const newParams = new URLSearchParams(params);
      newParams.delete("token");
      const newSearch = newParams.toString();
      const newPath = location.pathname + (newSearch ? `?${newSearch}` : "");
      navigate(newPath, { replace: true });
      // Set authenticated immediately since we just saved the token
      setIsAuthenticated(true);
      setLoading(false);
      return;
    }

    // Check for existing token
    const token = localStorage.getItem("token");
    const authenticated = !!token;
    setIsAuthenticated(authenticated);
    setLoading(false);
  }, [location.search, navigate]);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      const currentRoute = location.pathname + location.search;

      const currentEnv = (import.meta.env.NODE_ENV as Environment) || Environment.Development;

      document.cookie =
        `redirect=${encodeURIComponent(currentRoute)}; path=/` +
        (currentEnv === Environment.Production
          ? `; domain=.mjkochuk.com; secure; sameSite=None`
          : "");
      // In production, frontend and backend are on the same domain
      // Use VITE_API_URL if available, otherwise fall back to current origin
      const apiUrl = (import.meta.env.VITE_API_URL || window.location.origin).replace(/\/+$/, "");
      window.location.href = `${apiUrl}/login/github`;
    }
  }, [loading, isAuthenticated, location.pathname, location.search]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null; // Will redirect via useEffect
  }

  return children;
};

export default ProtectedRoute;
